/* eslint-disable @stylistic/ts/indent */
import { ReactNode } from "react";
import * as reactI18next from "react-i18next";
import { Card, Checkbox, Flex, Grid, Group, List, Stack, Text } from "@mantine/core";
import { get } from "lodash";
import { CampaignInquiryProduct } from "src/graphql/generated/graphql";
import { CampaignInquiryQueryDataType } from "src/hooks/campaignInquiries";
import { unsafeTranslate } from "src/utils/helpers";

import { ReviewDestinationType } from "../../../../hooks/reviews";
import { FormItem } from "../../../FormItem";
import { Link } from "../../../Link";
import { RichTextEditor } from "../../../RichTextEditor";

interface CampaignInquiryProductProps {
    product: CampaignInquiryProduct;
}

const CampaignInquiryProduct = ({ product }: CampaignInquiryProductProps) => {
    const { t } = reactI18next.useTranslation(["common"]);

    return (
        <Card p="md" shadow="sm" radius="md" withBorder>
            <Card.Section p="md">
                <Flex align="stretch">
                    <Grid style={{ flex: 1 }}>
                        <Grid.Col xs={12} md={6}>
                            <FormItem label={t("product_code_ean")}>
                                {product?.ean || <Text color="dimmed">-</Text>}
                            </FormItem>
                        </Grid.Col>
                        <Grid.Col xs={12} md={6}>
                            <FormItem label={t("productcategory")}>
                                {unsafeTranslate(`productcategory_${product?.category?.key}`, t) ||
                                    product?.category?.key}
                            </FormItem>
                        </Grid.Col>
                        <Grid.Col xs={12}>
                            <FormItem label={t("product_name")}>
                                {product?.name || <Text color="dimmed">-</Text>}
                            </FormItem>
                        </Grid.Col>
                        <Grid.Col xs={12} md={6}>
                            <FormItem label={t("product_manufacturer")}>
                                {product?.info?.manufacturer || <Text color="dimmed">-</Text>}
                            </FormItem>
                        </Grid.Col>
                        <Grid.Col xs={12} md={6}>
                            <FormItem label={t("product_model")}>
                                {product?.info?.model || <Text color="dimmed">-</Text>}
                            </FormItem>
                        </Grid.Col>
                        <Grid.Col xs={12}>
                            <FormItem mb="xs" label={t("product_description")}>
                                <RichTextEditor value={product?.description} readOnly />
                            </FormItem>
                        </Grid.Col>
                        <Grid.Col xs={12}>
                            <FormItem label={t("product_usermanuallink")}>
                                {product?.userManualLink || <Text color="dimmed">-</Text>}
                            </FormItem>
                        </Grid.Col>
                        <Grid.Col xs={12}>
                            <FormItem mb="xs" label={t("product_detailpagelinks")}>
                                <List>
                                    {(product?.detailPageLinks || []).map((link, index) => (
                                        <List.Item key={`${index}${link?.src}`}>
                                            <Text>{link?.label}</Text>
                                            <Text component={Link} color="dimmed" target="_blank" href={link?.src}>
                                                {link?.src}
                                            </Text>
                                        </List.Item>
                                    ))}
                                </List>
                            </FormItem>
                        </Grid.Col>
                    </Grid>
                </Flex>
            </Card.Section>
            <Card.Section py="md" inheritPadding withBorder>
                <FormItem label={t("campaign_productreviewdestinations")}>
                    {!product?.destinations?.length && <Text color="dimmed">-</Text>}
                    <List>
                        {(product?.destinations || []).map((destination, index) => {
                            const config = /^\{.*\}$/.test(destination?.config) ? JSON.parse(destination.config) : null;

                            let content: ReactNode;
                            switch (destination?.type) {
                                case ReviewDestinationType.tt:
                                    break;
                                case ReviewDestinationType.custom:
                                    content = (
                                        <>
                                            <Group mb="xs">
                                                <Checkbox
                                                    color="gray"
                                                    label={t(
                                                        "campaign_productreviewdestination_custom_confirmsubmission"
                                                    )}
                                                    checked={config?.confirmSubmission || false}
                                                    onChange={() => {}}
                                                />
                                                <Checkbox
                                                    color="gray"
                                                    label={t("campaign_productreviewdestination_custom_requirecontent")}
                                                    checked={config?.requireContent || false}
                                                    onChange={() => {}}
                                                />
                                            </Group>
                                            <FormItem
                                                label={t("campaign_productreviewdestination_custom_instructions")}
                                            >
                                                <RichTextEditor value={config?.instructions} readOnly />
                                            </FormItem>
                                        </>
                                    );
                                    break;
                                default:
                                    content = (
                                        <>
                                            {(destination?.configFields || []).map((field, index) => {
                                                let value: ReactNode = get(config, field.path);

                                                if (field.type === "url" && value) {
                                                    value = (
                                                        <Link target="_blank" href={value as string}>
                                                            {value}
                                                        </Link>
                                                    );
                                                }

                                                return (
                                                    <FormItem key={`${index}${field.path}`} label={field.label}>
                                                        {value || <Text color="dimmed">-</Text>}
                                                    </FormItem>
                                                );
                                            })}
                                        </>
                                    );
                            }

                            return (
                                <List.Item key={`${index}${destination?.type}`}>
                                    {destination?.name}
                                    {content}
                                </List.Item>
                            );
                        })}
                    </List>
                </FormItem>
            </Card.Section>
        </Card>
    );
};

export interface CampaignInquiryProductsProps {
    campaignInquiry: CampaignInquiryQueryDataType;
}

export const CampaignInquiryProducts = ({ campaignInquiry }: CampaignInquiryProductsProps) => {
    return (
        <Stack>
            {(campaignInquiry?.products || []).map((product, index) => (
                <CampaignInquiryProduct key={`${index}${product?.key}${product?.name}`} product={product} />
            ))}
        </Stack>
    );
};
